import { useState } from 'react';
import { FiEdit2, FiTrash2, FiHome, FiAlertTriangle, FiInfo, FiClock } from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import clsx from 'clsx';
import RoomHistoryDialog from './RoomHistoryDialog';

const RoomGrid = ({ rooms, onEdit, onDelete }) => {
  const [selectedRoom, setSelectedRoom] = useState(null);

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
  };

  const handleEditClick = (e, room) => {
    e.stopPropagation(); // Prevent opening history dialog
    onEdit(room);
  };

  const handleDeleteClick = (e, room) => {
    e.stopPropagation(); // Prevent opening history dialog
    onDelete(room);
  };
  const getStatusDisplay = (room) => {
    // Calcular el estado basado en current_status y has_incidents_today
    if (room.has_incidents_today) {
      return {
        label: 'Incidencia',
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <FiAlertTriangle className="h-3 w-3" />
      };
    }

    switch (room.current_status) {
      case 'maintenance':
        return {
          label: 'Mantenimiento',
          color: 'bg-purple-100 text-purple-800 border-purple-200',
          icon: null
        };
      case 'dirty':
        return {
          label: 'Pendiente',
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: null
        };
      case 'clean':
        return {
          label: 'Limpia',
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: null
        };
      default:
        return {
          label: 'Desconocido',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: null
        };
    }
  };

  if (rooms.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <FiHome className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No se encontraron habitaciones
        </h3>
        <p className="text-gray-500">
          No hay habitaciones que coincidan con los filtros actuales
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      {rooms.map((room, index) => {
          const statusDisplay = getStatusDisplay(room);

          return (
            <div
              key={room.id}
              onClick={() => handleRoomClick(room)}
              className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all duration-200 cursor-pointer animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              title="Ver historial de la habitación"
            >
            {/* Header con número de habitación y estado */}
            <div className="p-2 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <h3 className="text-xl font-bold text-gray-900">
                    {room.number}
                  </h3>
                  {room.notes && (
                    <div className="relative group">
                      <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
                      <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                        {room.notes}
                      </div>
                    </div>
                  )}
                </div>
                {/* Estado */}
                <span
                  className={clsx(
                    'inline-flex items-center space-x-1 text-xs font-semibold px-2.5 py-1 rounded-full border',
                    statusDisplay.color
                  )}
                >
                  {statusDisplay.icon}
                  <span>{statusDisplay.label}</span>
                </span>
              </div>
            </div>

            {/* Contenido - Camas y Botones */}
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                {/* Número de camas y tiempo de limpieza */}
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex items-center space-x-1.5 flex-shrink-0">
                    <IoBedOutline className="h-5 w-5 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      {room.bed_count}
                    </span>
                  </div>

                  {/* Tiempo estimado de limpieza */}
                  {room.cleaning_time_minutes && (
                    <div className="flex items-center space-x-1.5 flex-shrink-0">
                      <FiClock className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-600">
                        {room.cleaning_time_minutes} min
                      </span>
                    </div>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={(e) => handleEditClick(e, room)}
                    className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-md hover:bg-indigo-50 transition-colors"
                    title="Editar habitación"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => handleDeleteClick(e, room)}
                    className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                    title="Eliminar habitación"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Disposiciones de camas */}
              {room.bed_dispositions && room.bed_dispositions.length > 0 && (
                <div className="flex flex-wrap gap-1 min-w-0">
                  {room.bed_dispositions.map((disposition) => (
                    <span
                      key={disposition.id}
                      className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                    >
                      {disposition.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Room History Dialog */}
      {selectedRoom && (
        <RoomHistoryDialog
          room={selectedRoom}
          onClose={() => setSelectedRoom(null)}
        />
      )}
    </div>
  );
};

export default RoomGrid;